import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    getDisputeMessages,
    markDisputeRead,
    postDisputeMessage,
} from '../../api/backendClient';
import { resolveAttachmentUrl } from '../../utils/attachmentUrl';
import {
    fireErrorToast,
    shortenWalletAddress,
} from '../../utils/commonFunction';
import Loader from '../loader/Loader';

const roleLabel = (role) => {
    if (role === 'admin') return 'Admin';
    if (role === 'company') return 'Company';
    if (role === 'employee') return 'Employee';
    if (role === 'sender') return 'Sender';
    if (role === 'receiver') return 'Receiver';
    return 'Participant';
};

const roleIcon = (role, isSystem) => {
    if (isSystem) return 'fa-solid fa-circle-info';
    if (role === 'admin') return 'fa-solid fa-scale-balanced';
    if (role === 'company') return 'fa-regular fa-building';
    if (role === 'employee') return 'fa-regular fa-user';
    if (role === 'sender') return 'fa-solid fa-arrow-up';
    if (role === 'receiver') return 'fa-solid fa-arrow-down';
    return 'fa-regular fa-comment';
};

const formatMessageTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
        return date.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
        });
    }
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const messageLayout = (m) => {
    const isSystem = m.messageType === 'system';
    const role = m.author?.rolePreference;

    if (isSystem) {
        return {
            row: 'justify-center',
            bubble:
                'max-w-[95%] border border-amber-200/80 bg-amber-50/90 px-4 py-2.5 text-center shadow-none dark:border-amber-500/30 dark:bg-amber-950/35',
            meta: 'text-amber-800 dark:text-amber-200',
            body: 'text-amber-950/90 dark:text-amber-50/95',
            showAvatar: false,
        };
    }
    if (role === 'employee' || role === 'receiver') {
        return {
            row: 'justify-end',
            bubble:
                'max-w-[min(85%,28rem)] border border-teal-200/80 bg-teal-600 text-white shadow-sm dark:border-teal-500/40 dark:bg-teal-700',
            meta: 'text-teal-100/90',
            body: 'text-white',
            showAvatar: true,
        };
    }
    if (role === 'admin') {
        return {
            row: 'justify-start',
            bubble:
                'max-w-[min(85%,28rem)] border border-zinc-200 bg-zinc-100 shadow-sm dark:border-zinc-600 dark:bg-zinc-800/90',
            meta: 'text-zinc-700 dark:text-zinc-200',
            body: 'text-zinc-900 dark:text-zinc-100',
            showAvatar: true,
        };
    }
    if (role === 'company' || role === 'sender') {
        return {
            row: 'justify-start',
            bubble:
                role === 'sender'
                    ? 'max-w-[min(85%,28rem)] border border-violet-200/90 bg-violet-50 shadow-sm dark:border-violet-500/30 dark:bg-violet-950/35'
                    : 'max-w-[min(85%,28rem)] border border-sky-200/90 bg-sky-50 shadow-sm dark:border-sky-500/30 dark:bg-sky-950/35',
            meta:
                role === 'sender'
                    ? 'text-violet-900 dark:text-violet-200'
                    : 'text-sky-900 dark:text-sky-200',
            body: 'text-zinc-900 dark:text-zinc-100',
            showAvatar: true,
        };
    }
    return {
        row: 'justify-start',
        bubble:
            'max-w-[min(85%,28rem)] border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900/80',
        meta: 'text-zinc-600 dark:text-zinc-400',
        body: 'text-zinc-900 dark:text-zinc-100',
        showAvatar: true,
    };
};

const AvatarBadge = ({ role, isSystem }) => {
    const tones = isSystem
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
        : role === 'employee' || role === 'receiver'
          ? 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300'
          : role === 'admin'
          ? 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200'
          : role === 'company'
            ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300'
            : role === 'sender'
              ? 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300'
              : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300';

    return (
        <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${tones}`}
            aria-hidden
        >
            <i className={`${roleIcon(role, isSystem)} text-xs`} />
        </span>
    );
};

/**
 * @param {'jobs' | 'light' | 'dark'} variant — `light`/`jobs` for modals & jobs portal; `dark` for slate panels only
 */
const DisputeMessageThread = ({
    disputeId,
    authToken,
    pollIntervalMs = 20000,
    className = '',
    variant = 'light',
    readOnly = false,
    discussionMode = 'task',
}) => {
    const [messages, setMessages] = useState([]);
    const [disputeStatus, setDisputeStatus] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [draft, setDraft] = useState('');
    const [attachment, setAttachment] = useState(null);
    const listRef = useRef(null);
    const fileRef = useRef(null);

    const isDark = variant === 'dark';

    const scrollToBottom = () => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    };

    const fetchMessages = useCallback(
        async (silent) => {
            if (!disputeId || !authToken) return;
            try {
                if (!silent) setLoading(true);
                const data = await getDisputeMessages(authToken, disputeId);
                setMessages(data.messages || []);
                setUnreadCount(data.unreadCount || 0);
                setDisputeStatus(data.disputeStatus);
                if (!silent) {
                    await markDisputeRead(authToken, disputeId);
                    setUnreadCount(0);
                }
                setTimeout(scrollToBottom, 50);
            } catch (e) {
                if (!silent) {
                    fireErrorToast(
                        e?.response?.data?.message ||
                            'Could not load dispute messages',
                    );
                }
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [disputeId, authToken],
    );

    useEffect(() => {
        fetchMessages(false);
    }, [fetchMessages]);

    useEffect(() => {
        if (!disputeId || !authToken || pollIntervalMs <= 0) return undefined;
        const id = setInterval(() => fetchMessages(true), pollIntervalMs);
        return () => clearInterval(id);
    }, [disputeId, authToken, pollIntervalMs, fetchMessages]);

    const handleSend = async () => {
        const text = draft.trim();
        if ((!text && !attachment) || !authToken || !disputeId) return;
        try {
            setSending(true);
            const created = await postDisputeMessage(
                authToken,
                disputeId,
                text,
                attachment,
            );
            setDraft('');
            setAttachment(null);
            if (fileRef.current) fileRef.current.value = '';
            setMessages((prev) => [...prev, created]);
            await markDisputeRead(authToken, disputeId);
            setUnreadCount(0);
            setTimeout(scrollToBottom, 50);
        } catch (e) {
            fireErrorToast(
                e?.response?.data?.message || 'Failed to send message',
            );
        } finally {
            setSending(false);
        }
    };

    const closed =
        readOnly ||
        ['resolved', 'closed'].includes(String(disputeStatus || '').toLowerCase());

    if (!authToken) {
        return (
            <div
                className={`rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800/40 dark:bg-amber-950/30 dark:text-amber-100/90 ${className}`}
            >
                Connect your wallet and sign in to the backend, then reload to
                use dispute discussion.
            </div>
        );
    }

    const shellClass = isDark
        ? 'border-slate-700/80 bg-slate-950/50'
        : 'border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50';

    return (
        <div
            className={`flex flex-col overflow-hidden rounded-2xl border ${shellClass} ${className}`}
        >
            <div className="border-b border-zinc-100 bg-zinc-50/90 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/60">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-400">
                            <i className="fa-solid fa-comments text-sm" />
                        </span>
                        <div>
                            <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                                Discussion
                            </p>
                            <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                                {closed
                                    ? 'Thread closed — read only'
                                    : 'Updates every few seconds'}
                            </p>
                        </div>
                    </div>
                    {unreadCount > 0 ? (
                        <span className="rounded-full bg-teal-600 px-2.5 py-0.5 text-[10px] font-bold text-white shadow-sm">
                            {unreadCount} new
                        </span>
                    ) : null}
                </div>
            </div>

            <div
                ref={listRef}
                className="min-h-[10rem] max-h-[22rem] space-y-3 overflow-y-auto bg-gradient-to-b from-zinc-50/80 to-white px-3 py-4 dark:from-zinc-950/40 dark:to-zinc-950/20 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-300/80 dark:[&::-webkit-scrollbar-thumb]:bg-zinc-600/80 [&::-webkit-scrollbar-track]:bg-transparent"
            >
                {loading ? (
                    <div className="flex justify-center py-10">
                        <Loader size="28px" stroke={isDark ? '#38bdf8' : '#0d9488'} />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500">
                            <i className="fa-regular fa-message text-lg" />
                        </span>
                        <p className="mt-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">
                            No messages yet
                        </p>
                        <p className="mt-1 max-w-xs text-xs text-zinc-500">
                            {discussionMode === 'payment'
                                ? 'Share what happened. Sender, receiver, and dispute admin can reply here.'
                                : 'Share what happened. Company, employee, and dispute admin can reply here.'}
                        </p>
                    </div>
                ) : (
                    messages.map((m) => {
                        const a = m.author || {};
                        const isSystem = m.messageType === 'system';
                        const participantRole =
                            a.participantRole || a.rolePreference;
                        const layout = messageLayout({
                            ...m,
                            author: { ...a, rolePreference: participantRole },
                        });
                        const who = isSystem
                            ? 'System'
                            : a.name
                              ? a.name
                              : shortenWalletAddress(a.walletAddress);
                        const role = isSystem ? null : roleLabel(participantRole);

                        const alignEnd = layout.row === 'justify-end';

                        return (
                            <div
                                key={m.id}
                                className={`flex gap-2.5 ${layout.row}`}
                            >
                                {!alignEnd && layout.showAvatar ? (
                                    <AvatarBadge
                                        role={participantRole}
                                        isSystem={isSystem}
                                    />
                                ) : !layout.showAvatar ? null : (
                                    <span className="w-8 shrink-0" aria-hidden />
                                )}

                                <div
                                    className={`rounded-2xl px-3.5 py-2.5 text-sm ${layout.bubble}`}
                                >
                                    {!isSystem ? (
                                        <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                            <span
                                                className={`text-xs font-semibold ${layout.meta}`}
                                            >
                                                {who}
                                            </span>
                                            {role ? (
                                                <span
                                                    className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                                                    participantRole === 'employee' ||
                                                    participantRole === 'receiver'
                                                        ? 'bg-white/15 text-teal-50'
                                                        : participantRole === 'admin'
                                                          ? 'bg-zinc-200/80 text-zinc-800 dark:bg-zinc-600/80 dark:text-zinc-100'
                                                          : participantRole === 'company'
                                                            ? 'bg-sky-200/70 text-sky-900 dark:bg-sky-500/25 dark:text-sky-100'
                                                            : participantRole === 'sender'
                                                              ? 'bg-violet-200/70 text-violet-900 dark:bg-violet-500/25 dark:text-violet-100'
                                                            : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                                                    }`}
                                                >
                                                    {role}
                                                </span>
                                            ) : null}
                                            <span
                                                className={`ml-auto text-[10px] tabular-nums ${
                                                    participantRole === 'employee' ||
                                                    participantRole === 'receiver'
                                                        ? 'text-teal-100/80'
                                                        : 'text-zinc-400 dark:text-zinc-500'
                                                }`}
                                            >
                                                {formatMessageTime(m.createdAt)}
                                            </span>
                                        </div>
                                    ) : (
                                        <p
                                            className={`text-[10px] font-semibold uppercase tracking-wide ${layout.meta}`}
                                        >
                                            System · {formatMessageTime(m.createdAt)}
                                        </p>
                                    )}
                                    <p
                                        className={`whitespace-pre-wrap text-[13px] leading-relaxed [overflow-wrap:anywhere] ${layout.body}`}
                                    >
                                        {m.body}
                                    </p>
                                    {m.attachmentUrl || m.attachmentPath ? (
                                        <a
                                            href={resolveAttachmentUrl(
                                                m.attachmentUrl || m.attachmentPath,
                                            )}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={`mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold ${
                                                participantRole === 'employee' ||
                                                participantRole === 'receiver'
                                                    ? 'bg-white/15 text-white hover:bg-white/25'
                                                    : 'bg-teal-50 text-teal-800 hover:bg-teal-100 dark:bg-teal-500/15 dark:text-teal-300'
                                            }`}
                                        >
                                            <i className="fa-solid fa-paperclip text-[9px]" />
                                            Attachment
                                        </a>
                                    ) : null}
                                </div>

                                {alignEnd && layout.showAvatar ? (
                                    <AvatarBadge
                                        role={participantRole}
                                        isSystem={false}
                                    />
                                ) : alignEnd ? (
                                    <span className="w-8 shrink-0" aria-hidden />
                                ) : null}
                            </div>
                        );
                    })
                )}
            </div>

            {!closed ? (
                <div className="border-t border-zinc-100 bg-zinc-50/50 p-3 dark:border-zinc-800 dark:bg-zinc-900/40">
                    <div className="rounded-xl border border-zinc-200 bg-white p-2 shadow-sm dark:border-zinc-700 dark:bg-zinc-950">
                        <textarea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    if (!sending && (draft.trim() || attachment)) {
                                        handleSend();
                                    }
                                }
                            }}
                            placeholder="Write a message… (Enter to send, Shift+Enter for new line)"
                            rows={2}
                            className="w-full resize-none border-0 bg-transparent px-2 py-1.5 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:ring-0 dark:text-zinc-100"
                            disabled={sending}
                        />
                        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-100 px-2 py-2 dark:border-zinc-800">
                            <div className="flex min-w-0 items-center gap-2">
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                                    className="hidden"
                                    onChange={(e) =>
                                        setAttachment(e.target.files?.[0] || null)
                                    }
                                />
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[11px] font-semibold text-zinc-700 transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                                >
                                    <i className="fa-solid fa-paperclip text-[10px]" />
                                    {attachment ? (
                                        <span className="max-w-[140px] truncate">
                                            {attachment.name}
                                        </span>
                                    ) : (
                                        'Attach'
                                    )}
                                </button>
                                {attachment ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setAttachment(null);
                                            if (fileRef.current) {
                                                fileRef.current.value = '';
                                            }
                                        }}
                                        className="text-[11px] text-zinc-500 hover:text-rose-600"
                                    >
                                        Clear
                                    </button>
                                ) : null}
                            </div>
                            <button
                                type="button"
                                onClick={handleSend}
                                disabled={sending || (!draft.trim() && !attachment)}
                                className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-500 disabled:opacity-50 dark:bg-teal-600 dark:hover:bg-teal-500"
                            >
                                <i className="fa-solid fa-paper-plane text-[10px]" />
                                {sending ? 'Sending…' : 'Send'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="border-t border-zinc-100 px-4 py-3 text-center text-xs text-zinc-500 dark:border-zinc-800">
                    This dispute is closed. No new messages can be sent.
                </div>
            )}
        </div>
    );
};

export default DisputeMessageThread;
